import { RelatedEntityType } from "@dune/types";
import { DEFAULT_EMAIL_DOMAIN } from "../environment/environment";

const UUID_REGEX =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUUID(uuid: string): boolean {
	return UUID_REGEX.test(uuid);
}

export function generateRoutingEmail(
	entityType: RelatedEntityType,
	uuid: string,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	if (!uuid || typeof uuid !== "string") {
		throw new Error("UUID parameter is required and must be a string");
	}

	if (!isValidUUID(uuid)) {
		throw new Error(
			`Invalid UUID format: ${uuid}. Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`,
		);
	}

	const prefixMap: Record<RelatedEntityType, string> = {
		BROKER: "apporteur",
		CONSTRUCTION_SITE: "chantier",
		PROPOSITION: "proposition",
		GFA: "chantierGFA",
	};

	const prefix = prefixMap[entityType];
	if (!prefix) {
		throw new Error(`Unsupported entity type: ${entityType}`);
	}

	return `${prefix}+${uuid}@${domain}`;
}

export function generateBrokerEmail(
	emailRoutingUuid: string,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	return generateRoutingEmail("BROKER", emailRoutingUuid, domain);
}

export function generateBrokerEmailWithFallback(
	emailRoutingUuid: string | null | undefined,
	brokerId: number,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	if (emailRoutingUuid && isValidUUID(emailRoutingUuid)) {
		return generateBrokerEmail(emailRoutingUuid, domain);
	}

	const prefix = "apporteur";
	return `${prefix}+${brokerId}@${domain}`;
}

export function generateConstructionSiteEmail(
	emailRoutingUuid: string,
	isGFA: boolean,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	return generateRoutingEmail(
		isGFA ? "GFA" : "CONSTRUCTION_SITE",
		emailRoutingUuid,
		domain,
	);
}

export function generateConstructionSiteEmailWithFallback(
	emailRoutingUuid: string | null | undefined,
	constructionSiteId: number,
	isGFA: boolean,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	if (emailRoutingUuid && isValidUUID(emailRoutingUuid)) {
		return generateConstructionSiteEmail(emailRoutingUuid, isGFA, domain);
	}

	const prefix = "chantier";
	return `${prefix}${isGFA ? "GFA" : ""}+${constructionSiteId}@${domain}`;
}

export function generatePropositionEmail(
	emailRoutingUuid: string,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	return generateRoutingEmail("PROPOSITION", emailRoutingUuid, domain);
}

export function generatePropositionEmailWithFallback(
	emailRoutingUuid: string | null | undefined,
	propositionId: number,
	domain: string = DEFAULT_EMAIL_DOMAIN,
): string {
	if (emailRoutingUuid && isValidUUID(emailRoutingUuid)) {
		return generatePropositionEmail(emailRoutingUuid, domain);
	}

	const prefix = "proposition";
	return `${prefix}+${propositionId}@${domain}`;
}
